// ─── Provider constants ───────────────────────────────────────────────────────

/**
 * Mirrors MarketplaceProviders from Beetegre.Domain/Marketplaces.
 * String keys must match the backend constants exactly (stored as strings in DB/API).
 */
export const MarketplaceProvider = {
  Trendyol:    'TRENDYOL',
  Hepsiburada: 'HEPSIBURADA',
  N11:         'N11',
  Ciceksepeti: 'CICEKSEPETI',
  Pazarama:    'PAZARAMA',
  Beecommerce: 'BEECOMMERCE',
  // AB#1861/AB#1864 — mirrors Beetegre.Domain/Marketplaces/MarketplaceProviders.Shopify. Stores
  // are never created through the credential form (App Store 2.3.1 forbids collecting a shop
  // address in-app); this only lets an already-claimed Shopify store render correctly in the
  // list/tabs. See StoreCredentialFields, which deliberately excludes it from the New Store
  // provider picker.
  Shopify:     'SHOPIFY',
} as const;

export type MarketplaceProviderValue = typeof MarketplaceProvider[keyof typeof MarketplaceProvider];

/**
 * Brand names shown to users in place of the DB code. Mirrors MarketplaceProviders.DisplayNames in
 * Beetegre.Domain/Marketplaces; proper nouns, identical in tr and en.
 */
export const MARKETPLACE_PROVIDER_DISPLAY_NAMES: Record<MarketplaceProviderValue, string> = {
  [MarketplaceProvider.Trendyol]:    'Trendyol',
  [MarketplaceProvider.Hepsiburada]: 'Hepsiburada',
  [MarketplaceProvider.N11]:         'N11',
  [MarketplaceProvider.Ciceksepeti]: 'Çiçeksepeti',
  [MarketplaceProvider.Pazarama]:    'Pazarama',
  [MarketplaceProvider.Beecommerce]: 'Beecommerce',
  [MarketplaceProvider.Shopify]:     'Shopify',
};

/**
 * Ordered list of all supported marketplace provider codes.
 * Single source of truth for dropdowns, filters, and sidebar module checks.
 */
export const MARKETPLACE_PROVIDERS: MarketplaceProviderValue[] = [
  MarketplaceProvider.Trendyol,
  MarketplaceProvider.Hepsiburada,
  MarketplaceProvider.N11,
  MarketplaceProvider.Ciceksepeti,
  MarketplaceProvider.Pazarama,
  MarketplaceProvider.Beecommerce,
  MarketplaceProvider.Shopify,
];

/**
 * Providers that run an inbound catalog sync job, so MarketplaceStore.AutoImportUnmapped has an
 * effect. Mirrors MarketplaceJobTypes.ProductSyncProviders in Beetegre.Domain; Ciceksepeti has no
 * product-sync job and the flag is inert there.
 */
export const AUTO_IMPORT_UNMAPPED_PROVIDERS: readonly string[] = [
  MarketplaceProvider.Trendyol,
  MarketplaceProvider.Hepsiburada,
  MarketplaceProvider.N11,
  MarketplaceProvider.Pazarama,
  MarketplaceProvider.Beecommerce,
  MarketplaceProvider.Shopify,
];

/**
 * Subset of AUTO_IMPORT_UNMAPPED_PROVIDERS whose sync actually binds unmapped rows to local
 * products. Pazarama's and Shopify's sync do not (AutoBindsLocalProducts is false there); for them
 * the flag only gates the missing-image sweep.
 */
export const AUTO_BIND_LOCAL_PRODUCTS_PROVIDERS: readonly string[] =
  AUTO_IMPORT_UNMAPPED_PROVIDERS.filter(
    p => p !== MarketplaceProvider.Pazarama && p !== MarketplaceProvider.Shopify,
  );

export const supportsAutoImportUnmapped = (provider: string | null | undefined): boolean =>
  !!provider && AUTO_IMPORT_UNMAPPED_PROVIDERS.includes(provider);

export const autoBindsLocalProducts = (provider: string | null | undefined): boolean =>
  !!provider && AUTO_BIND_LOCAL_PRODUCTS_PROVIDERS.includes(provider);

/**
 * Providers with a marketplace order-sync job (backend: the job adapters that map
 * MarketplaceStoreJobAction.OrderSync). Auto-invoice can only be switched on once that sync is current,
 * so stores of other providers (Ciceksepeti, Shopify) are never offered the setting.
 */
export const ORDER_SYNC_PROVIDERS: readonly string[] = [
  MarketplaceProvider.Trendyol,
  MarketplaceProvider.Hepsiburada,
  MarketplaceProvider.N11,
  MarketplaceProvider.Pazarama,
  MarketplaceProvider.Beecommerce,
];

export const supportsAutoInvoice = (provider: string | null | undefined): boolean =>
  !!provider && ORDER_SYNC_PROVIDERS.includes(provider);

// ─── Product status constants ──────────────────────────────────────────────────

/**
 * Mirrors MarketplaceProductStatuses from Beetegre.Domain/Marketplaces.
 * Canonical vocabulary served by GET /api/v1/marketplace/statuses; clients should
 * compare against these constants instead of inline string literals.
 */
export const MarketplaceProductStatus = {
  OnSale:          'onSale',
  NotOnSale:       'notOnSale',
  Approved:        'approved',
  PendingApproval: 'pendingApproval',
  Rejected:        'rejected',
  Archived:        'archived',
  Blacklisted:     'blacklisted',
  Locked:          'locked',
  Blocked:         'blocked',
  Draft:           'draft',
} as const;

export type MarketplaceProductStatusValue = typeof MarketplaceProductStatus[keyof typeof MarketplaceProductStatus];

// ─── Listing detail DTO ────────────────────────────────────────────────────────

/** Mirrors MarketplaceListingDetailDto from Beetegre.Shared.DTOs.Tenant. */
export interface MarketplaceListingDetailDto {
  provider: string;
  storeName: string;
  listingStatus: string;
  lastSendError: string | null;
  /** Last time this row was written. Syncs that skip unchanged rows leave it at the last content change. */
  lastSyncedAt: string | null;
  /** Start time (UTC ISO) of the owning store's last successful product sync, FULL or DELTA. */
  storeProductsSyncedAt: string | null;
  listPrice: number;
  salePrice: number;
  /** ISO 4217 currency for listPrice / salePrice / marketplacePriceOverride. */
  currency: string;
  marketplacePriceOverride: number | null;
  vatRate: number;
  marketplaceQuantity: number;
  localStockQuantity: number;
  categoryPath: string | null;
  brandName: string | null;
  attributes: Array<{ name: string; value: string }>;
  /** Capped at 5 most-recent jobs by the backend; render as-is, do not re-slice. */
  recentJobs: Array<{
    jobId: string;
    jobType: string;
    status: string;
    occurredAt: string;
    message: string | null;
  }>;
  /**
   * Public storefront URL for this listing, built by the backend MarketplaceProductUrls
   * service. Null when the identifier required for the provider is missing — the
   * "Pazaryerinde Aç" affordance should be disabled in that case.
   */
  marketplaceUrl: string | null;
  /** Owning MarketplaceStore.Id — scopes the manual price/inventory update action to this listing's store only. */
  storeId: string;
  /** Local Product.Id this listing is linked to; null when unmatched. */
  localProductId: string | null;
}

// ─── Listing tab — drawer + chip cell shared shape ─────────────────────────────

/**
 * Single referenced listing inside the drawer's tab strip (and the chip cell's
 * onOpenDetail payload). Imported by both ListingDetailDrawer and MarketplaceSummaryCell
 * so the contract has one source of truth — no duplicate `interface ListingTab` in the
 * cell + drawer.
 */
export interface ListingTab {
  listingId: string;
  storeName: string;
  /** Distinguishing label for the drawer's picker (variant value for a variant parent).
   *  Falls back to storeName when absent. */
  label?: string;
  /** Pre-translated sale-status label (e.g. "On Sale") shown beside the picker label. */
  statusLabel?: string;
  /** Status dot colour (data-driven; design-system-policy §3 exemption). */
  statusColor?: string;
}

// ─── Outbound status constants ─────────────────────────────────────────────────

/**
 * Mirrors MarketplaceOutboundStatuses from Beetegre.Domain/Marketplaces.
 * Used for outbound operation status comparisons in product list, listing detail,
 * and the MarketplaceSync filter/log panels.
 */
export const MarketplaceOutboundStatus = {
  Pending:         'pending',
  Sent:            'sent',
  Approved:        'approved',
  Rejected:        'rejected',
  Failed:          'failed',
  WaitingApproval: 'waitingApproval',
  TimedOut:        'timedout',
} as const;

export type MarketplaceOutboundStatusValue = typeof MarketplaceOutboundStatus[keyof typeof MarketplaceOutboundStatus];

// ─── Outbound operation constants ──────────────────────────────────────────────

/**
 * Mirrors MarketplaceOutboundOperations from Beetegre.Domain/Marketplaces.
 * LastOutboundOperation is stored as a comma-separated list of these values;
 * split on ',' and compare each part against these constants.
 */
export const MarketplaceOutboundOperation = {
  Create:     'create',
  Update:     'update',
  Price:      'price',
  Stock:      'stock',
  PriceStock: 'priceStock',
} as const;

export type MarketplaceOutboundOperationValue = typeof MarketplaceOutboundOperation[keyof typeof MarketplaceOutboundOperation];

// ─── Listing filter constants ──────────────────────────────────────────────────

/**
 * Mirrors MarketplaceSyncFilters from Beetegre.Domain/Marketplaces.
 * Wire values must match the backend constants exactly (query-string parameter).
 * Backend also defines `all`; the UI currently uses its own `__all__` sentinel
 * for the "no filter" dropdown option, so the `All` member is omitted until
 * a call site actually sends `all` over the wire.
 */
export const MarketplaceListingFilter = {
  Linked:   'linked',
  Unlinked: 'unlinked',
  Archived: 'archived',
  OnSale:   'onSale',
} as const;

export type MarketplaceListingFilterValue = typeof MarketplaceListingFilter[keyof typeof MarketplaceListingFilter];

// ─── Job status constants ──────────────────────────────────────────────────────

/**
 * Mirrors JobStatus enum from Beetegre.Domain/Entities (lowercase — backend serializes
 * via .ToLowerInvariant() so values match JSON casing used in the listing detail drawer).
 */
export const MarketplaceJobStatus = {
  Pending:   'pending',
  Running:   'running',
  Completed: 'completed',
  Failed:    'failed',
  Cancelled: 'cancelled',
} as const;

export type MarketplaceJobStatusValue = typeof MarketplaceJobStatus[keyof typeof MarketplaceJobStatus];

/** One job queued by `update-price-inventory`; HB and Pazarama queue two per store (price + stock). */
export interface QueuedPriceInventoryJob {
  jobId:   string;
  storeId: string;
  jobType: string;
}

/** `POST marketplace-sync/retry` response; `count` is 0 when every selected listing was skipped (inactive store, same-type job already pending/running, banned or unsupported listing). `message` is legacy Turkish and must not be rendered. */
export interface RetryListingResponse {
  message:               string;
  count:                 number;
  storesSkippedInactive: number;
  /** Stores skipped because a same-type job was already pending/running. */
  storesSkippedPending:  number;
  blacklistedCount:      number;
  /** Selected listings on a provider that cannot be retried; skipped. */
  unsupportedCount:      number;
}

/** `POST marketplace-sync/update-by-products` 202 response; `message` is legacy Turkish and must not be rendered. */
export interface UpdateByProductsResponse {
  message:               string;
  count:                 number;
  storesSkippedInactive: number;
  storesSkippedPending:  number;
}

/** `POST marketplace-sync/update-price-inventory` response (MarketplaceSyncEndpoints.UpdatePriceInventoryResponse). */
export interface UpdatePriceInventoryResponse {
  message:               string;
  count:                 number;
  mode:                  string;
  storesSkippedInactive: number;
  storesSkippedPending:  number;
  jobs:                  QueuedPriceInventoryJob[];
}

// ─── Local category -> integration category attribute schema ──────────────────

/**
 * Mirrors the `status` values of
 * GET /api/v1/tenants/{tenantId}/local-categories/{localCategoryId}/marketplace-mappings/provider/{provider}/attributes.
 * Wire values are camelCase strings and must match the backend exactly.
 */
export const CategoryAttributeSchemaStatus = {
  Ready:       'ready',
  NotMapped:   'notMapped',
  NotSynced:   'notSynced',
  Unsupported: 'unsupported',
  CatalogUnlinked: 'catalogUnlinked',
  NotLeaf:     'notLeaf',
} as const;

export type CategoryAttributeSchemaStatusValue =
  typeof CategoryAttributeSchemaStatus[keyof typeof CategoryAttributeSchemaStatus];

export interface CategoryAttributeSchemaAttribute {
  id: string;
  name: string;
  required: boolean;
  varianter: boolean;
  slicer: boolean;
  allowCustom: boolean;
  allowMultiple: boolean;
  valueCount: number;
  sampleValues: string[];
}

export interface CategoryAttributeSchemaResponse {
  status: CategoryAttributeSchemaStatusValue;
  catalogCategoryName: string | null;
  catalogCategoryPath: string | null;
  isLeaf: boolean;
  requiresVariantAxis: boolean;
  totalAttributeCount: number;
  attributes: CategoryAttributeSchemaAttribute[];
}

// ─── Local category / brand -> integration mapping panels ────────────────────

export interface LocalCategoryProviderMapping {
  provider: string;
  storeCount: number;
  externalCategoryId: number | null;
  externalCategoryName: string | null;
  externalCategoryPath: string | null;
  isLeaf: boolean;
  isAutoLinked: boolean;
  /**
   * Beecommerce gibi magaza kapsamli katalog tutan pazaryerlerinde satir MAGAZA basinadir;
   * provider geneli katalog tutanlarda (Trendyol/HB/N11/Pazarama) null gelir.
   */
  storeId: string | null;
  storeName: string | null;
  /** Linked catalog category; null on legacy mappings that predate the catalog backfill. */
  catalogCategoryId?: string | null;
}

export interface ProviderCategorySearchItem {
  externalCategoryId: number;
  externalCategoryName: string;
  externalCategoryPath: string | null;
  isLeaf: boolean;
  catalogCategoryId?: string | null;
  externalCategoryCode?: string | null;
}

export interface LocalBrandProviderMapping {
  provider: string;
  storeCount: number;
  externalBrandId: number | null;
  externalBrandName: string | null;
  externalBrandIdString: string | null;
  isAutoLinked: boolean;
}

export interface BrandSearchItem {
  id: number;
  name: string;
  externalBrandIdString?: string | null;
  catalogBrandId?: string | null;
}

